import React, { useState } from 'react';
import { Calendar, MapPin, Users, Filter, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { upcomingEvents } from '../mock';
import { useTranslation } from '../i18n/LanguageContext';

const CATEGORIES = [
  { en: 'All', es: 'Todos' },
  { en: 'Worship', es: 'Adoración' },
  { en: 'Retreat', es: 'Retiro' },
  { en: 'Outreach', es: 'Alcance' },
  { en: 'Conference', es: 'Conferencia' },
  { en: 'Training', es: 'Capacitación' }
];

const EventsPage = () => {
  const { t, lang } = useTranslation();
  const { toast } = useToast();
  const [selectedKey, setSelectedKey] = useState('All');
  const dateLocale = lang === 'es' ? 'es-ES' : 'en-US';

  const filteredEvents = selectedKey === 'All'
    ? upcomingEvents
    : upcomingEvents.filter(event => (event.category?.en ?? event.category) === selectedKey);

  const handleRegister = (eventTitle, registrationLink) => {
    if (registrationLink) {
      window.open(registrationLink, '_blank');
    } else {
      toast({
        title: t('common.registerNow'),
        description: eventTitle
      });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="text-white py-24 md:py-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/holy-fire-bg.jpg')" }}></div>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6 fade-in">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 uppercase tracking-[0.3em] text-xs">
              {t('events.badge')}
            </Badge>
            <h1 className="display-hero text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              {t('events.heroTitle')} <span className="italic font-light">{t('events.heroAccent')}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto">
              {t('events.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-background border-b sticky top-16 z-40 backdrop-blur-sm bg-background/95">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 overflow-x-auto pb-2">
            <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <div className="flex space-x-2">
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat.en}
                  variant={selectedKey === cat.en ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedKey(cat.en)}
                  className="btn-hover whitespace-nowrap"
                >
                  {t(cat)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-muted-foreground">{t('events.noEventsTitle')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {filteredEvents.map((event) => (
                <Card key={event.id} className="overflow-hidden card-hover group">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={event.image}
                      alt={t(event.title)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-primary text-primary-foreground shadow-lg">
                        {t(event.category)}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white mb-1">
                        {t(event.title)}
                      </h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-muted-foreground mb-6">
                      {t(event.description)}
                    </p>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-sm">
                        <Calendar className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
                        <span className="font-medium">
                          {new Date(event.date).toLocaleDateString(dateLocale, {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      {event.time && (
                        <div className="flex items-center text-sm">
                          <Users className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
                          <span>{t(event.time)}</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm">
                        <MapPin className="h-5 w-5 mr-3 text-primary flex-shrink-0" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                    {(() => {
                      if (!event.registrationOpen) return null;

                      if (event.registrationLinks) {
                        return (
                          <div className="space-y-3">
                            <Button
                              className="w-full btn-hover"
                              size="lg"
                              onClick={() => window.open(event.registrationLinks.student, '_blank')}
                            >
                              {t('home.hsStudentReg')}
                              <ExternalLink className="ml-2 h-5 w-5" />
                            </Button>
                            <Button
                              className="w-full btn-hover"
                              size="lg"
                              variant="outline"
                              onClick={() => window.open(event.registrationLinks.counselor, '_blank')}
                            >
                              {t('home.hsCounselorReg')}
                              <ExternalLink className="ml-2 h-5 w-5" />
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <Button
                          className="w-full btn-hover"
                          size="lg"
                          onClick={() => handleRegister(t(event.title), event.registrationLink)}
                        >
                          {t('common.registerNow')}
                          <ExternalLink className="ml-2 h-5 w-5" />
                        </Button>
                      );
                    })()}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
